import React from 'react';

import { Stack, TableCell, TableCellProps, Typography, styled } from '@mui/material';

export const Title = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: '1.875rem',
  lineHeight: '1.875rem',
  textAlign: 'center',
  margin: 0,
  marginBottom: '1.2rem',
}));

export const EmptyHeadCell = styled((props: TableCellProps) => <TableCell {...props} />)({
  maxWidth: '20rem',
  borderWidth: 0,
  paddingTop: 0,
  paddingBottom: 0,
});

export const HeadCell = styled(TableCell)({
  padding: '0 0.5rem 0.5rem 0',
  borderBottom: 'none',
});

export const GrayBox = styled(Stack)({
  borderColor: '#E6EAEF',
  borderStyle: 'solid',
  borderWidth: 1,
  backgroundColor: '#F7F8FA',
  borderTopLeftRadius: '0.5rem',
  borderTopRightRadius: '0.5rem',
  width: '100%',
  height: '100%',
  padding: '1rem 1.5rem',
});

export const LevelBox = styled(Stack, {
  shouldForwardProp: (propName: string) => !['currentLevel', 'level'].includes(propName),
})<{ level: string; currentLevel: string }>(({ currentLevel, level }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow:
    parseInt(currentLevel?.replace('level', '')) <= parseInt(level.replace('level', ''))
      ? ''
      : '0px 2px 6px rgba(151, 166, 190, 0.2)',
  border:
    parseInt(currentLevel?.replace('level', '')) <= parseInt(level.replace('level', ''))
      ? '3px solid rgba(69, 146, 250, 0.3)'
      : 'none',
  borderRadius: '0.4rem',
  backgroundColor: 'white',
  width: '100%',
  padding: '0.5rem 1.5rem',
}));

export const PermissionNameCell = styled(TableCell)({
  maxWidth: '20rem',
  borderWidth: 0,
  paddingRight: '0.5rem',
  paddingTop: 0,
  paddingBottom: 0,
});

export const RoleName = styled(Stack, {
  shouldForwardProp: (propName: string) => !['rowKey', 'allPermissionsCount'].includes(propName),
})<{ rowKey: number; allPermissionsCount: number }>(({ rowKey, allPermissionsCount }) => ({
  borderColor: '#E6EAEF',
  borderStyle: 'solid',
  borderWidth: 1,
  backgroundColor: '#F7F8FA',
  borderTopLeftRadius: rowKey > 0 ? 0 : '0.5rem',
  borderTopRightRadius: rowKey > 0 ? 0 : '0.5rem',
  borderBottomLeftRadius: rowKey === allPermissionsCount - 1 ? '0.5rem' : 0,
  borderBottomRightRadius: rowKey === allPermissionsCount - 1 ? '0.5rem' : 0,
  padding: '1rem 1.5rem',
  height: '3.3125rem !important',
}));

export const PermissionCheckboxCell = styled(TableCell)(() => ({
  padding: 0,
  paddingRight: '0.5rem',
  border: 'none',
}));

export const PermissionBox = styled(Stack, {
  shouldForwardProp: (propName: string) => !['rowKey', 'allPermissionsCount'].includes(propName),
})<{ rowKey: number; allPermissionsCount: number }>(({ rowKey, allPermissionsCount }) => ({
  borderColor: '#E6EAEF',
  borderStyle: 'solid',
  borderLeftWidth: 1,
  borderRightWidth: 1,
  borderBottomWidth: rowKey === allPermissionsCount - 1 ? 1 : 0,
  borderTopWidth: !rowKey ? 1 : 0,
  borderBottomLeftRadius: rowKey === allPermissionsCount - 1 ? '0.5rem' : 0,
  borderBottomRightRadius: rowKey === allPermissionsCount - 1 ? '0.5rem' : 0,

  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',

  height: '3.3125rem !important',
}));

export const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgb(16 22 26 / 40%)'
      : 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
      : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 'rgba(206,217,224,.5)',
  },
}));

export const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#4592FA',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#4592FA',
  },
});
